import React, { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { useStore } from "../store";
import { fadeGroup, towerOpacity, wtcOpacity, clamp01 } from "./fade";
import { envState } from "./env";

// Simple box building.
function B({ p, w, d, h, color, metal = 0.15, rough = 0.65 }) {
  return (
    <mesh position={[p[0], h / 2, p[1]]} castShadow receiveShadow>
      <boxGeometry args={[w, h, d]} />
      <meshStandardMaterial color={color} metalness={metal} roughness={rough} />
    </mesh>
  );
}

// Construction tower crane: mast + horizontal jib + counter-jib.
function Crane({ p, h }) {
  return (
    <group position={[p[0], 0, p[1]]}>
      <mesh position={[0, h / 2, 0]}>
        <boxGeometry args={[2, h, 2]} />
        <meshStandardMaterial color="#d0a02a" metalness={0.4} roughness={0.5} />
      </mesh>
      <mesh position={[6, h, 0]}>
        <boxGeometry args={[26, 1.4, 1.4]} />
        <meshStandardMaterial color="#d0a02a" metalness={0.4} roughness={0.5} />
      </mesh>
      <mesh position={[0, h + 3, 0]}>
        <boxGeometry args={[2.4, 3, 2.4]} />
        <meshStandardMaterial color="#3a3a3a" />
      </mesh>
    </group>
  );
}

// The 16-acre WTC complex, temporally cross-faded with the Twin Towers / Memorial Pools.
export default function WTCComplex() {
  const era2001 = useRef();
  const present = useRef();
  const construction = useRef();

  useFrame(() => {
    const t = useStore.getState().temporal;
    // 2001 low-rise complex shares the Twin Towers' fade-out curve.
    fadeGroup(era2001.current, towerOpacity(t));
    // Completed present-era buildings share 1 WTC's fade-in curve.
    fadeGroup(present.current, wtcOpacity(t));
    // WTC 2 & 5 appear only through the middle "construction" years.
    const c = clamp01(Math.min((t - 2004) / 2.5, (2016 - t) / 5));
    fadeGroup(construction.current, c);
    // Warm lit windows at dusk/night across the whole complex.
    const glow = envState.night * 1.6;
    [era2001.current, present.current].forEach((g) => {
      g &&
        g.traverse((o) => {
          if (o.material && o.material.color) {
            o.material.emissive = o.material.emissive || new THREE.Color();
            o.material.emissive.set("#ffcf87");
            o.material.emissiveIntensity = glow;
          }
        });
    });
  });

  return (
    <group>
      {/* ===== 2001: WTC 3 (Marriott), 4, 5, 6, 7 — warm limestone/granite ===== */}
      <group ref={era2001}>
        <B p={[0, 92]} w={40} d={64} h={73} color="#b8ad98" metal={0.1} rough={0.75} />        {/* WTC 3 Marriott */}
        <B p={[96, 96]} w={70} d={70} h={36} color="#c2b7a2" metal={0.1} rough={0.75} />        {/* WTC 4 */}
        <B p={[96, -96]} w={70} d={70} h={34} color="#c2b7a2" metal={0.1} rough={0.75} />       {/* WTC 5 */}
        <B p={[-116, -110]} w={82} d={70} h={30} color="#c8bda8" metal={0.1} rough={0.75} />    {/* WTC 6 Customs */}
        <B p={[-42, -216]} w={58} d={74} h={186} color="#a7b0ba" metal={0.4} rough={0.35} /> {/* WTC 7 (dark granite/glass) */}
      </group>

      {/* ===== Present: WTC 3, 4, 7 (blue glass) + Westfield Mall. Oculus lives in OneWTC. ===== */}
      <group ref={present}>
        <B p={[74, 96]} w={46} d={46} h={329} color="#7d98b8" metal={0.65} rough={0.14} /> {/* 3 WTC */}
        <B p={[104, 58]} w={44} d={44} h={297} color="#8aa2bd" metal={0.65} rough={0.14} /> {/* 4 WTC */}
        <B p={[-42, -216]} w={56} d={68} h={226} color="#9fb8cf" metal={0.6} rough={0.16} /> {/* 7 WTC */}
        {/* Westfield / WTC Transportation Hall mall podium */}
        <B p={[46, 66]} w={130} d={86} h={14} color="#cfd6db" metal={0.2} rough={0.5} />
      </group>

      {/* ===== Mid-transition: WTC 2 & 5 under construction ===== */}
      <group ref={construction}>
        <B p={[86, -40]} w={48} d={48} h={132} color="#8c8c88" metal={0.1} rough={0.85} /> {/* 2 WTC core */}
        <Crane p={[86, -40]} h={168} />
        <B p={[96, -96]} w={62} d={62} h={92} color="#8c8c88" metal={0.1} rough={0.85} /> {/* 5 WTC core */}
        <Crane p={[96, -96]} h={124} />
      </group>
    </group>
  );
}
